import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, Calendar, LayoutGrid, MapPin, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_7adffd06-a6e7-4f95-8888-9b22d8068cd7/artifacts/1m4vs7t5_Ceramic%20Artist%20at%20Outdoor%20Market.png";

// Months rendered on the calendar view. Keeping this data-driven so we can
// swap the window (or make it dynamic) in one place.
const CALENDAR_MONTHS = [
  { year: 2026, month: 8, label: "September 2026" }, // month is 0-indexed
  { year: 2026, month: 9, label: "October 2026" },
  { year: 2026, month: 10, label: "November 2026" },
];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseISODateLocal(s) {
  // ISO date-only strings ("YYYY-MM-DD") get parsed by `new Date(...)` as UTC
  // midnight, which then rolls back a day in every US timezone when formatted.
  // Parse the parts manually so the date matches exactly what admins entered.
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function formatDateRange(start, end) {
  if (!start) return "";
  const opts = { month: "long", day: "numeric", year: "numeric" };
  try {
    const s = parseISODateLocal(start).toLocaleDateString("en-US", opts);
    if (!end || end === start) return s;
    const e = parseISODateLocal(end).toLocaleDateString("en-US", opts);
    return `${s} – ${e}`;
  } catch {
    return start;
  }
}

// Format a JS Date as "YYYY-MM-DD" in local time (matches admin-entered strings).
function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Build a Map<"YYYY-MM-DD", Event[]> where each event appears on every day it spans.
function buildEventsByDate(events) {
  const byDate = new Map();
  for (const ev of events) {
    const start = parseISODateLocal(ev.start_date);
    if (!start) continue;
    const end = parseISODateLocal(ev.end_date) || start;
    const cursor = new Date(start);
    // Guard against runaway loops if someone enters end < start
    let safety = 366;
    while (cursor <= end && safety-- > 0) {
      const key = ymdLocal(cursor);
      const bucket = byDate.get(key) || [];
      bucket.push(ev);
      byDate.set(key, bucket);
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return byDate;
}

// Return the 7×N grid of day cells for a given (year, monthIndex).
// Leading + trailing spillover days from adjacent months are included so the
// weekday columns line up. We flag those with `outOfMonth: true`.
function buildMonthGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const startWeekday = first.getDay(); // 0 = Sunday
  const cells = [];
  // Leading days from the prior month
  for (let i = startWeekday; i > 0; i--) {
    const d = new Date(year, monthIndex, 1 - i);
    cells.push({ date: d, outOfMonth: true });
  }
  // Current month
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, monthIndex, d), outOfMonth: false });
  }
  // Trailing days to complete the last week
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, outOfMonth: true });
  }
  return cells;
}

function MonthCalendar({ year, monthIndex, label, eventsByDate }) {
  const cells = useMemo(() => buildMonthGrid(year, monthIndex), [year, monthIndex]);
  const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  return (
    <div
      className="rounded-2xl border border-[color:var(--ed-forest)]/15 bg-white shadow-sm overflow-hidden"
      data-testid={`event-calendar-month-${monthKey}`}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--ed-forest)]/10 bg-[color:var(--ed-cream)]/60">
        <h3 className="font-editorial text-2xl leading-none text-[color:var(--ed-forest-dark)]">
          {label}
        </h3>
      </div>
      <div className="grid grid-cols-7 text-[11px] uppercase tracking-widest text-[color:var(--ed-muted)] bg-white border-b border-[color:var(--ed-forest)]/10">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="px-2 py-2 text-center font-body">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-fr">
        {cells.map((cell, i) => {
          const key = ymdLocal(cell.date);
          const dayEvents = eventsByDate.get(key) || [];
          const isToday = ymdLocal(new Date()) === key;
          return (
            <div
              key={`${key}-${i}`}
              className={`min-h-[92px] border-t border-l border-[color:var(--ed-forest)]/10 p-1.5 flex flex-col gap-1 ${
                cell.outOfMonth ? "bg-[color:var(--ed-cream)]/25 text-[color:var(--ed-muted)]/60" : "bg-white"
              } ${i % 7 === 6 ? "border-r-0" : ""}`}
              data-testid={cell.outOfMonth ? undefined : `calendar-day-${key}`}
            >
              <span
                className={`text-xs font-body ${
                  isToday
                    ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-[color:var(--ed-terracotta)] text-white"
                    : cell.outOfMonth
                    ? "text-[color:var(--ed-muted)]/50"
                    : "text-[color:var(--ed-forest-dark)]"
                }`}
              >
                {cell.date.getDate()}
              </span>
              <div className="flex-1 space-y-1 overflow-hidden">
                {!cell.outOfMonth && dayEvents.slice(0, 3).map((ev) => (
                  <Link
                    key={`${key}-${ev.slug}`}
                    to={`/events/${ev.slug}`}
                    className="block truncate text-[11px] leading-tight px-1.5 py-1 rounded bg-[color:var(--ed-terracotta)]/12 text-[color:var(--ed-terracotta)] hover:bg-[color:var(--ed-terracotta)] hover:text-white transition-colors"
                    title={ev.title}
                    data-testid={`calendar-event-link-${ev.slug}-${key}`}
                  >
                    {ev.title}
                  </Link>
                ))}
                {!cell.outOfMonth && dayEvents.length > 3 && (
                  <span className="text-[10px] text-[color:var(--ed-muted)] pl-1">
                    +{dayEvents.length - 3} more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PublicEventsListPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid"); // "grid" | "calendar"

  useEffect(() => {
    fetch(`${API}/events`)
      .then((r) => r.json())
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const eventsByDate = useMemo(() => buildEventsByDate(events), [events]);

  return (
    <div className="editorial min-h-screen" data-testid="public-events-list-page">
      <Helmet>
        <title>Events – Artisan Market & Studio</title>
        <meta name="description" content="Upcoming curated artisan events, craft shows, and community markets by Artisan Market & Studio." />
      </Helmet>

      {/* Hero banner */}
      <section className="relative overflow-hidden" data-testid="events-hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="A ceramic artist arranging handmade pottery at an outdoor artisan market"
            className="w-full h-full object-cover"
            data-testid="events-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E3227]/92 from-0% via-[#1E3227]/70 via-40% to-[#1E3227]/25 to-100%" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[color:var(--ed-cream)]" />
        </div>

        <div
          className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 md:pt-36 md:pb-28 min-h-[62vh] flex flex-col justify-end text-[#FDF7EA]"
          style={{ textShadow: "0 2px 16px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.5)" }}
        >
          <span
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--ed-gold)]"
            data-testid="events-hero-kicker"
          >
            <Sparkles className="w-3.5 h-3.5" />
            The Season Ahead
          </span>
          <h1
            className="mt-4 text-5xl md:text-7xl font-editorial leading-[1.02] max-w-3xl"
            data-testid="events-hero-title"
          >
            Made by two hands,<br />shared in good company.
          </h1>
          <p className="mt-6 font-body text-lg md:text-xl max-w-3xl text-[#FDF7EA]/95 leading-relaxed">
            As a collective of artists, we celebrate making more than selling. Our events are a
            tribute to what two hands can do when ignited with passion. Come visit us at one of
            these events and see what we&apos;re all about.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pt-16 md:pt-20 pb-32">
        {/* View toggle */}
        <div className="mb-10 flex items-center justify-between gap-4 flex-wrap">
          <p className="font-body text-[color:var(--ed-muted)]">
            {loading
              ? "\u00A0"
              : events.length > 0
              ? `${events.length} upcoming event${events.length === 1 ? "" : "s"}`
              : ""}
          </p>
          <div
            className="inline-flex rounded-full border border-[color:var(--ed-forest)]/20 bg-white p-1"
            role="tablist"
            aria-label="Events view"
            data-testid="events-view-toggle"
          >
            <button
              type="button"
              onClick={() => setView("grid")}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-body transition-colors ${
                view === "grid"
                  ? "bg-[color:var(--ed-terracotta)] text-white"
                  : "text-[color:var(--ed-forest-dark)] hover:bg-[color:var(--ed-cream)]"
              }`}
              aria-pressed={view === "grid"}
              data-testid="events-view-grid-btn"
            >
              <LayoutGrid className="w-4 h-4" />
              Grid
            </button>
            <button
              type="button"
              onClick={() => setView("calendar")}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-body transition-colors ${
                view === "calendar"
                  ? "bg-[color:var(--ed-terracotta)] text-white"
                  : "text-[color:var(--ed-forest-dark)] hover:bg-[color:var(--ed-cream)]"
              }`}
              aria-pressed={view === "calendar"}
              data-testid="events-view-calendar-btn"
            >
              <Calendar className="w-4 h-4" />
              Calendar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-[color:var(--ed-muted)]" data-testid="events-loading">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="card-warm text-center" data-testid="events-empty">
            <p className="font-editorial text-2xl">Nothing on the calendar just yet.</p>
            <p className="mt-3 font-body text-[color:var(--ed-muted)]">Check back soon &mdash; we&apos;re planning the next one.</p>
          </div>
        ) : view === "grid" ? (
          <div className="grid md:grid-cols-2 gap-8" data-testid="events-grid-view">
            {events.map((ev, idx) => (
              <Link
                key={ev.id}
                to={`/events/${ev.slug}`}
                className="group card-warm hover:shadow-xl transition-all duration-300 overflow-hidden p-0"
                data-testid={`event-card-${ev.slug}`}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                {ev.hero_image_url && (
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={ev.hero_image_url}
                      alt={ev.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {ev.featured && (
                      <span className="absolute top-4 left-4 bg-[color:var(--ed-terracotta)] text-[#FDF7EA] px-3 py-1 text-xs rounded-full font-semibold tracking-wide uppercase">
                        Featured
                      </span>
                    )}
                  </div>
                )}
                <div className="p-6">
                  {ev.tagline && <span className="kicker">{ev.tagline}</span>}
                  <h2 className="mt-3 text-3xl leading-tight group-hover:text-[color:var(--ed-terracotta)] transition-colors">
                    {ev.title}
                  </h2>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 font-body text-sm text-[color:var(--ed-muted)]">
                    {ev.start_date && (
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {formatDateRange(ev.start_date, ev.end_date)}
                      </span>
                    )}
                    {(ev.venue || ev.location) && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        {[ev.venue, ev.location].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-[color:var(--ed-forest-dark)] font-semibold">
                    Read the story
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-10" data-testid="events-calendar-view">
            {CALENDAR_MONTHS.map((m) => (
              <MonthCalendar
                key={`${m.year}-${m.month}`}
                year={m.year}
                monthIndex={m.month}
                label={m.label}
                eventsByDate={eventsByDate}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
